import { AttachmentMedia, cn, Tooltip, TooltipContent, TooltipTrigger } from "@pdfking/components"
import type { AnyNode, ArrayForm, ObjectForm } from "@pdfking/forms"
import type { PageNumber } from "@pdfking/pdfengine"
import { QuantityToString } from "@pdfking/quantity"
import { DISTANCE } from "@pdfking/quantity/constants"
import { CreateRenderFunction, NextFrame } from "@pdfking/reactivity/preact"
import type { FileName } from "@pdfking/structs"
import { NullQ } from "@pdfking/structs"
import { Constant } from "@pdfking/tfunc"
import { CopyIcon, FileTextIcon, MoveIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { useContext } from "preact/hooks"

import { ExternalLink } from "#compatibility"
import type { FileCtx } from "#public"
import type { FileCollectionStore } from "#public"
import type { ResultContext } from "#public"

import { PdfviewRouter } from "../app/PdfviewRouter"
import { range_for } from "../controller/RangeFor"
import { BuildFileCtx } from "../forms/BuildFileCtx"
import { Dispatch } from "../forms/dispatch/Dispatch"
import { child_path } from "../forms/dispatch/DomId"
import { is_visible } from "../forms/dispatch/FieldLayout"
import { DjLabel } from "../forms/fields/DjLabel"
import { useTranslation, useTranslationTyped } from "../i18n/useTranslation"
import { WithDroppedFiles } from "../utils/WithDroppedFiles"
import { FileMedia } from "./FileMedia"
import { TooltipIconButton } from "./TooltipIconButton"
import { UploadCard } from "./UploadCard"

type RowForm = ObjectForm<Record<string, unknown>>

// The fields a row carries next to its file, in display order. `file_id` is
// deliberately not one of them: the file cell IS the row's identity — a row
// gets its file from its own upload cell (or by being duplicated), never from
// a select over the other rows' files.
const FIELD_KEYS = ["file_range", "file_destination", "file_scale"] as const

// The drag payload a row reorder carries — what tells it apart from an OS file
// drag for anything else listening (see pdfview-app's NativeDrop).
const ROW_MIME = "application/x-pdfview-file-row"

/**
 * The unified file panel: upload and assignment as one list of rows, one per
 * `file_key` entry. A row with a file shows its preview + name/size/pages next
 * to the row's own fields (range, destination, scale); a blank row (the "+"
 * button appends one) shows an upload cell in the file slot instead, and the
 * upload lands in that very row. Each row can be duplicated to the end, and
 * dragged by its handle to reorder; the -/+ pair above the list removes the
 * last row / appends a blank one — all of it just edits the `file_key` array
 * node, and the viewer's `sync_files` keeps the file store reconciled.
 */
export const FileRows = CreateRenderFunction({
  computed: {
    ctx(): FileCtx {
      return BuildFileCtx(this)
    },
    file_key(): ArrayForm<unknown> | undefined {
      return this.node?.results.file_key as ArrayForm<unknown> | undefined
    },
    field_keys(): string[] {
      return FIELD_KEYS.filter(key => is_visible(key, this.ctx))
    },
    // An even split, not the form grids' `col_fr` weights: the file cell
    // already takes a fixed slice off the front of this line, so a field
    // weighted double (scale) starves the ones beside it — enough that a plain
    // "1-19" range clipped under its own clear button. `minmax(0, …)` keeps a
    // wide value from pushing its column past its share.
    template(): string {
      return this.field_keys.map(Constant("minmax(0, 1fr)")).join(" ")
    },
  },
  data: {
    // The row index a drag started from. While set, that row is lifted out of
    // the list entirely and `drag_slot` holds the gap it would drop into.
    drag_index: undefined as number | undefined,
    // Where the lifted row would land, as an index into the list *without* it
    // (0…n-1) — which is exactly the `to` that `move` takes. The placeholder
    // renders in this gap, so the rows below it have already shifted down by
    // the time the drop happens: what you see is what you get.
    drag_slot: undefined as number | undefined,
    // The lifted row's pixel height, measured at dragstart — the placeholder
    // copies it so the list doesn't jump the moment the row leaves the flow.
    drag_height: 0,
  },
  methods: {
    // The drop placeholder, sized to the lifted row — one element whether it
    // draws between rows or as the tail slot.
    drop_gap() {
      return (
        <div
          className="rounded-lg border-2 border-dashed border-ring bg-accent/40"
          key="gap"
          style={{ height: `${this.drag_height}px` }}
        />
      )
    },
    inputs(): unknown[] {
      return this.file_key?.results.map(child => child.input) ?? []
    },
    commit(next: unknown[]) {
      this.file_key?.validate(next)
    },
    // A blank-pages row: no `file_id` is what makes it blank (see the file cell
    // in the render), and one page is the smallest thing it can mean — seeded
    // explicitly rather than left undefined so its select opens on a real value
    // instead of an empty slot the user has to notice and fill.
    add() {
      this.commit([...this.inputs(), { file_range: range_for(1) }])
    },
    duplicate(index: number) {
      const inputs = this.inputs()
      this.commit([...inputs, inputs[index]])
    },
    // Drops one row wherever it sits — the counterpart of the header's `-`,
    // which can only take the last one.
    remove(index: number) {
      this.commit(this.inputs().filter((_, i) => i !== index))
    },
    move(from: number, to: number) {
      if (from === to) return
      const inputs = this.inputs()
      const [row] = inputs.splice(from, 1)
      inputs.splice(to, 0, row)
      this.commit(inputs)
    },
    // The order rows are drawn in while a drag is in flight: the lifted row
    // moved to the end, everything else closing up in front of it. Without a
    // drag this is just 0…n-1.
    //
    // Moved and hidden rather than dropped from the list, because that row is
    // the drag source: unmount it and the gesture loses the node `dragend`
    // fires on, so a cancelled drag would leave the list stuck in its lifted
    // state forever. Keeping the same `key` also means preact moves the
    // existing DOM node instead of building a new one, so the live drag keeps
    // its element.
    display_order(): number[] {
      const all = this.file_key?.results.map((_, i) => i) ?? []
      if (NullQ(this.drag_index)) return all
      return [...all.filter(i => i !== this.drag_index), this.drag_index]
    },
    // Which gap the pointer is over: the half of the row it sits in decides
    // whether the lifted row lands before or after it.
    slot_at(slot: number, e: DragEvent): number {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      return e.clientY > rect.top + rect.height / 2 ? slot + 1 : slot
    },
    drag_over(slot: number, e: DragEvent) {
      if (NullQ(this.drag_index)) return
      // Claiming the drag is what makes the drop land here rather than the
      // browser rejecting it (and what keeps the cursor showing "move").
      e.preventDefault()
      this.drag_slot = this.slot_at(slot, e)
    },
    drop_row(e: DragEvent) {
      if (NullQ(this.drag_index) || NullQ(this.drag_slot)) return
      e.preventDefault()
      this.move(this.drag_index, this.drag_slot)
      this.drag_end()
    },
    drag_end() {
      this.drag_index = undefined
      this.drag_slot = undefined
    },
  },
  props: {
    hidden_fields: [] as string[],
    node: undefined as ObjectForm<Record<string, unknown>> | undefined,
    on_files: undefined as ((files: File[]) => void) | undefined,
    staff: false,
    store: undefined as unknown as FileCollectionStore,
    view: undefined as ResultContext | undefined,
  },
  render() {
    const node = this.file_key
    if (!node) return undefined
    const actions = useTranslationTyped("actions")
    const pdfview = useTranslationTyped("pdfview")
    const errors = useTranslation("errors")
    const pdfview_router = useContext(PdfviewRouter)
    return (
      // The whole list accepts the drop, not just the rows: the natural place
      // to release is the placeholder itself, and there are the flex gaps
      // between rows too. Neither is a row, so without this the browser refuses
      // the drop and cancels the drag — the row would spring back to where it
      // started. The rows' own `dragover` still owns *which* slot; this only
      // says "yes, you may let go here", keeping whatever slot they last set.
      <div
        className="flex flex-col gap-2"
        onDragOver={(e: DragEvent) => {
          if (!NullQ(this.drag_index)) e.preventDefault()
        }}
        onDrop={this.drop_row}
      >
        {/* One header line for the whole list, the labels sitting over their
            columns — the rows themselves render label-less (`hide_labels`).
            It mirrors a row's structure rather than guessing at an offset: the
            same leading width (the file cell), the same `gap-2` and horizontal
            padding, and the same grid template as the fields below — so the two
            line up at any sidebar width, the way the compact array form does it
            (see Dispatch's ArrayField). The trailing spacer is `aria-hidden`
            since it exists only to hold the columns apart.

            With no rows there are no columns to head, and the labels would sit
            over the two dashed buttons naming nothing. */}
        {!!node.results.length && (
          <div className="flex items-center gap-2 px-2">
            {/* The file cell gets its label like any other column. It reads off
              `file_id` — the field the cell stands in for, even though the cell
              is a preview and a name rather than an input (see FIELD_KEYS). */}
            <div className="w-52 shrink-0">
              <DjLabel field_id="file_id" path={child_path("file_key", 0, "file_id")} />
            </div>
            <div
              className="grid min-w-0 flex-1 gap-x-2"
              style={{ gridTemplateColumns: this.template }}
            >
              {this.field_keys.map(key => (
                <DjLabel field_id={key} key={key} path={child_path("file_key", 0, key)} />
              ))}
            </div>
            {/* Holds the labels clear of the row's three trailing controls
              (duplicate, delete, handle) and their gaps. */}
            <div aria-hidden className="h-6 w-[5.5rem] shrink-0" />
          </div>
        )}
        {this.display_order().flatMap((index, slot) => {
          const item = node.results[index]
          const row = item as RowForm
          const name = String(row.results.file_id.input ?? "") as FileName
          const entry = name ? this.store.files[name] : undefined
          const first = entry?.pages[1 as PageNumber]
          const error = entry?.error
          // Where "open this file on its own" goes — the host's url for a
          // viewer holding just this file. Built per render rather than inside
          // a click, because it is an `href` on a real anchor now and a link
          // has to know where it points before anyone follows it (hover,
          // middle-click, copy link). One encoded route per file row per
          // render, which is cheap beside the row itself.
          const open_href = entry
            ? pdfview_router.blank_href(
                WithDroppedFiles({ files: {}, imposition_data: {} }, { [name]: entry.file })
              )
            : undefined
          // `store.error` covers two very different failures: the split refused
          // the file (it never opened — nothing to assign, so the row gives its
          // controls up for the message), or one page failed to *rasterize*,
          // which costs a preview and nothing else. Only the first is fatal,
          // and a page count is what tells them apart: the file opened.
          const failed = !!error && !entry?.page_count
          // Between "added" and "opened": the store entry exists but the split
          // has not reported a page count yet (see CreateFileStore — it starts
          // undefined).
          const loading = !!entry && !error && NullQ(entry.page_count)
          // The row under the cursor: still mounted (it owns the drag), but out
          // of the flow, so it is neither a drop target nor a gap position.
          const lifted = this.drag_index === index
          // The gap the lifted row would drop into, drawn in the flow at its
          // live position — so the rows past it have already moved aside and
          // the list reads as the result of letting go.
          const gap = !lifted && this.drag_slot === slot && this.drop_gap()
          const card = (
            <div
              className={cn(
                "flex items-center gap-2 rounded-lg border bg-card p-2",
                lifted && "hidden",
                // A file that failed to open is the only thing worth reading in
                // its row, so the row says so on its own border too.
                failed && "border-destructive/50"
              )}
              key={index}
              onDragOver={(e: DragEvent) => this.drag_over(slot, e)}
              onDrop={this.drop_row}
            >
              {entry ? (
                // A failed row has given up its field columns (below), so the
                // name and the error take the whole line instead of wrapping
                // inside the fixed file column — the message is the only thing
                // left to read, and it is usually a sentence.
                <div
                  className={cn(
                    "flex items-center gap-2",
                    failed ? "min-w-0 flex-1" : "w-52 shrink-0"
                  )}
                >
                  {/* A second *viewer* on this one file, rather than the raw
                      blob in the browser's own PDF reader: it opens the very
                      route a drop lands on (hence `WithDroppedFiles` over an
                      empty state), so the file arrives with a row of its own
                      and every tool this viewer has. Where that url opens — a
                      browser tab, or a window the desktop host builds — is the
                      build's to answer, see ExternalLinkType. No url means the
                      host routes nowhere, so the preview renders unlinked
                      rather than as a link that does nothing. */}
                  {open_href ? (
                    <ExternalLink
                      aria-label={actions.OPEN()}
                      className="shrink-0 cursor-pointer"
                      href={open_href}
                    >
                      <FileMedia store={entry} />
                    </ExternalLink>
                  ) : (
                    <FileMedia store={entry} />
                  )}
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium" title={name}>
                      {name}
                    </span>
                    {error ? (
                      <span className="text-xs text-destructive">
                        {errors[error.enum]?.(error) ?? error.enum}
                      </span>
                    ) : (
                      // Size and page count read as one line — two facts about
                      // the same file, not two rows of metadata.
                      <span className="truncate text-xs text-muted-foreground">
                        {[
                          first &&
                            QuantityToString(first.trim, DISTANCE, {
                              unit: "cm",
                              decimals: 1,
                            }),
                          entry.page_count ? pdfview.PAGE_COUNT({ count: entry.page_count }) : "…",
                        ]
                          .filter(Boolean)
                          .join(" - ")}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                // A row with no `file_id` *is* the blank-pages row — the
                // representation the pipeline already expects (see
                // ResolveFilePages: "blank rows (`file_id` is optional) resolve
                // no media and fall back to the sample size"). Its `file_range`
                // says how many blanks, so it reads and edits like any other
                // row; only the file cell differs.
                <div className="flex w-52 shrink-0 items-center gap-2">
                  <AttachmentMedia>
                    <FileTextIcon />
                  </AttachmentMedia>
                  <span className="truncate text-sm font-medium">{pdfview.BLANK_PAGE()}</span>
                </div>
              )}

              {/* A failed file has nothing to assign — a range, layer and scale
                  over a file that never opened are noise in front of the one
                  thing worth reading, so the row gives the space up entirely
                  and keeps only the way out (delete) and reorder. */}
              {failed ? undefined : (
                <div
                  className="grid min-w-0 flex-1 items-start gap-x-2"
                  style={{ gridTemplateColumns: this.template }}
                >
                  {this.field_keys.map(key => (
                    <Dispatch
                      cell={true}
                      ctx={this.ctx}
                      // Nothing to decide until the split reports back: the
                      // range is empty because the page count is still unknown,
                      // so editing it now would only be overwritten by
                      // `fill_range`.
                      disabled={loading}
                      field_key={key}
                      hide_labels={true}
                      key={key}
                      node={row.results[key] as AnyNode}
                      parent={row}
                      path={child_path("file_key", index, key)}
                    />
                  ))}
                </div>
              )}

              {/* The row's own controls, together at the right end. Copying a
                  file that never opened would only duplicate the error, so a
                  failed row drops the button rather than showing it greyed. */}
              {!failed && (
                <TooltipIconButton
                  disabled={loading || node.results.length >= node.max}
                  onClick={() => this.duplicate(index)}
                  side="top"
                  title={actions.COPY()}
                  variant="outline"
                >
                  <CopyIcon />
                </TooltipIconButton>
              )}

              <TooltipIconButton
                className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={node.results.length <= node.min}
                onClick={() => this.remove(index)}
                side="top"
                title={actions.DELETE()}
                variant="outline"
              >
                <Trash2Icon />
              </TooltipIconButton>

              {/* Only the handle is draggable — not the row. Making the row the
                  drag source needs a "did this start on the handle?" gate, or
                  selecting text in a range field tears the row out; the gate has
                  to be read synchronously in `dragstart`, and getting that wrong
                  silently kills the whole gesture. The row-sized drag image
                  doesn't need it: `setDragImage` can point at any element, so it
                  points at the row while the handle stays the source. */}
              <Tooltip>
                {/* `asChild` renders no wrapper of its own, so the handle stays
                    a direct child of the row — which `setDragImage` below
                    relies on to find it. */}
                <TooltipTrigger asChild>
                  <div
                    className="flex size-6 shrink-0 cursor-move items-center justify-center text-muted-foreground hover:text-foreground"
                    draggable
                    onDragEnd={this.drag_end}
                    onDragStart={(e: DragEvent) => {
                      if (!e.dataTransfer) return
                      e.dataTransfer.effectAllowed = "move"
                      // Firefox and Safari refuse to start a drag that carries
                      // no data at all; the payload is the row index, so a
                      // listener that only wants OS file drags (see
                      // pdfview-app's NativeDrop) tells this apart by its type.
                      e.dataTransfer.setData(ROW_MIME, String(index))
                      // Snapshot the whole row before it leaves the flow — left
                      // implicit, the browser would drag the 16px handle icon.
                      const box = (e.currentTarget as HTMLElement).parentElement!
                      const rect = box.getBoundingClientRect()
                      e.dataTransfer.setDragImage(box, e.clientX - rect.left, e.clientY - rect.top)
                      this.drag_height = rect.height
                      // Lifting the row unmounts it (it drops out of
                      // `display_order`) — and with it this handle, the drag
                      // source. Do that in the same tick as `dragstart` and the
                      // browser sees its source node vanish mid-gesture: the
                      // drag never starts, and `dragend` never fires to clean
                      // up. One frame later the gesture owns itself.
                      NextFrame(() => {
                        this.drag_index = index
                        this.drag_slot = slot
                      })
                    }}
                  >
                    <MoveIcon className="size-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">{pdfview.REORDER()}</TooltipContent>
              </Tooltip>
            </div>
          )
          return gap ? [gap, card] : [card]
        })}
        {/* The tail gap: dropping past the last row appends. Every other slot
            is drawn above, before its row — this one has no row after it to
            hang off. The last slot is `n - 1`, not `n`, because the lifted row
            is still in the list (hidden, at the end) but is not a slot. */}
        {this.drag_slot === node.results.length - 1 && this.drop_gap()}

        {/* The two ways to grow the list, side by side under it: bring pages in
            from a file, or add blanks. Both append a row; dashed, because
            neither is a row yet. Hidden once the array is full — `max` is the
            schema's own bound (16, see FilesForm), not ours to exceed. With no
            rows yet there is nothing to pad, so only the upload half shows —
            and `flex-1` alone means it spans the whole width. */}
        {node.results.length < node.max && (
          <div className="flex gap-2">
            <UploadCard className="flex-1" on_files={this.on_files} />

            {!!node.results.length && (
              <button
                // `bg-card` to match the UploadCard beside it — the pair reads
                // as two halves of one affordance, and on a translucent host a
                // bare background here made the left one white and this one
                // glass.
                className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed bg-card p-2 text-left transition-colors hover:bg-accent"
                onClick={this.add}
                type="button"
              >
                <AttachmentMedia>
                  <PlusIcon />
                </AttachmentMedia>
                <span className="truncate text-sm font-medium">{pdfview.ADD_BLANK_PAGES()}</span>
              </button>
            )}
          </div>
        )}
      </div>
    )
  },
})
